import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import {
  ApiResponse,
  Raffle,
  WsEvents,
  TopWinner,
  UnclaimedReward,
  RaffleStatus
} from './raffles.entity';
import { RafflesService } from './raffles.service';

@Injectable({ providedIn: 'root' })
export class RafflesState {
  private readonly rafflesService = inject(RafflesService);
  public readonly rafflesList = signal<Raffle[]>([]);
  public readonly unclaimedRaffles = signal<UnclaimedReward[]>([]);
  public readonly topWinners = signal<TopWinner[]>([]);

  load(): Observable<ApiResponse.RafflesList> {
    return this.rafflesService.getRaffles().pipe(
      tap((response: ApiResponse.RafflesList) => this.rafflesList.set(response.raffles))
    );
  }

  removeRaffle(raffleId: number, iterationId: number): void {
    const newUnclaimedList = this.unclaimedRaffles().filter(item => 
      item.raffleId !== raffleId && item.raffleIterationId !== iterationId);
    const newRaffleList = this.rafflesList().filter(item => item.raffle.id !== raffleId && item.id !== iterationId);
    
    this.unclaimedRaffles.set(newUnclaimedList);
    this.rafflesList.set(newRaffleList);
  }
  
  getUnclaimedRaffles(): Observable<ApiResponse.UnclaimedRewardsList> {
    return this.rafflesService.getUnclaimedRaffles().pipe(
      tap((response: ApiResponse.UnclaimedRewardsList) => {
        this.unclaimedRaffles.set(response.unclaimedRewards);
      })
    );
  }

  joinRaffle(raffleId: number): Observable<ApiResponse.ParticipantJoined> {
    return this.rafflesService.joinRaffle(raffleId).pipe(
      tap(() => {
        const newRaffleList = this.rafflesList().map(item => {
          if (item.raffle.id === raffleId) {
            return {...item, participant: true };
          }

          return item;
        });

        this.rafflesList.set(newRaffleList);
      })
    );
  }

  claimReward(raffleId: number, iterationId: number): Observable<ApiResponse.ParticipantJoined> {
    return this.rafflesService.claimReward(raffleId, iterationId).pipe(
      tap(() => {
        const newRaffleList = this.rafflesList().map(item => {
          if (item.raffle.id === raffleId) {
            return {...item, status: 3 };
          }

          return item;
        });

        this.rafflesList.set(newRaffleList);
      })
    );
  }

  updateRaffleInfo(): Observable<ApiResponse.RafflesList> {
    return this.rafflesService.getRaffles().pipe(
      tap((response: ApiResponse.RafflesList) => {
        this.rafflesList.set(response.raffles);
      })
    );
  }

  getTopWinners(raffleId: number): Observable<ApiResponse.TopWinners> {
    return this.rafflesService.getTopWinners(raffleId).pipe(
      tap((response: ApiResponse.TopWinners) => {
        this.topWinners.set(response.winners);
      })
    );
  }

  onWsWinnerAnnounce(data: WsEvents.WinnerAnnounce): void {
    const rafflesList = this.rafflesList();

    if (!rafflesList) {
      return;
    }

    const newRaffleList = rafflesList.map(item => {
      if (item.raffle.id === data.raffleId) {
        return {...item, winner: data.winner };
      }

      return item;
    });

    this.rafflesList.set(newRaffleList);
  }

  onWsUpdateParticipants(data: WsEvents.UpdateParticipants): void {
  
    const rafflesList = this.rafflesList();

    if (!rafflesList) {
      return;
    }

    const newRaffleList = rafflesList.map(item => {
      if (item.raffle.id === data.raffleId) {
        return {...item, amountOfParticipants: data.participantsAmount };
      }

      return item;
    });

    this.rafflesList.set(newRaffleList);
  }

  onWsSetCanceledRaffle(data: WsEvents.onWsSetCanceledRaffle): void {
  
    const rafflesList = this.rafflesList();

    if (!rafflesList) {
      return;
    }

    const newRaffleList = rafflesList.map(item => {
      if (item.raffle.id === data.raffleId) {
        return {...item, status: RaffleStatus.STATUS_NOT_STARTED };
      }

      return item;
    });

    this.rafflesList.set(newRaffleList);
  }

}